import { useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import { CLICK_CODE, isEmptyArray } from '@local/utils'

import { TimelineTab } from '@web/federation/gql/graphql'
import sendClickLog from '@web/libs/nlog/click/sendClickLog'
import { removeQueryStringWithoutRouterChange } from '@web/libs/utils'
import { useUpcomingMarkers } from '@web/pages/timeline/components/TimelineUpcoming/useUpcomingMarkers'
import { useTimelineContext } from '@web/pages/timeline/index.context'
import { UPCOMING_SECTION_TAB } from '@web/pages/v5/home/UpcomingSection'
import { useUpcomingBooking } from '@web/pages/v5/home/UpcomingSection/hooks/useUpcomingBooking'
import { useUpcomingMore } from '@web/pages/v5/home/UpcomingSection/hooks/useUpcomingMore'
import { useUpcomingOrder } from '@web/pages/v5/home/UpcomingSection/hooks/useUpcomingOrder'
import { useUpcomingTrainTicket } from '@web/pages/v5/home/UpcomingSection/hooks/useUpcomingTrainTicket'
import UITimelineAllUpcomingSection from '@web/ui/pages/timeline/components/UpcomingSection/UITimelineAllUpcomingSection'
import UITimelinePaginationUpcomingSection from '@web/ui/pages/timeline/components/UpcomingSection/UITimelinePaginationUpcomingSection'

export const UPCOMING_PAGE_SIZE = 10

// 탭 업커밍 첫 노출시에는 3+1(딤드) = 4개만 먼저 노출합니다
const FIRST_DISPLAY_COUNT = 4

interface Props {
  onClickMore?: () => void
}

export const TimelineUpcomingSection = ({ onClickMore }: Props) => {
  const { tab } = useTimelineContext()
  const { query } = useRouter()

  const upcomingAnchorId = query.upcomingAnchor

  const [firstDisplay, setFirstDisplay] = useState(!!upcomingAnchorId ? false : true)

  const {
    upcomingBookingItems,
    upcomingBookingCount,
    upcomingBookingFetchMore,
    upcomingBookingMore,
    resetUpcomingBooking,
    data: upcomingBookingData,
    loading: bookingLoading,
  } = useUpcomingBooking({ limit: UPCOMING_PAGE_SIZE, skip: ![TimelineTab.All, TimelineTab.Reservation].includes(tab) })

  const {
    upcomingOrderItems,
    upcomingOrderCount,
    upcomingOrderFetchMore,
    upcomingOrderMore,
    resetUpcomingOrder,
    data: upcomingOrderData,
    loading: orderLoading,
  } = useUpcomingOrder({
    limit: UPCOMING_PAGE_SIZE,
    skip: ![TimelineTab.All, TimelineTab.Order].includes(tab),
  })

  const {
    upcomingTrainTicketItems,
    upcomingTrainTicketCount,
    upcomingTrainTicketFetchMore,
    upcomingTrainTicketMore,
    resetUpcomingTrainTicket,
    loading: trainLoading,
  } = useUpcomingTrainTicket({
    limit: UPCOMING_PAGE_SIZE,
    skip: ![TimelineTab.All, TimelineTab.TrainTicket].includes(tab),
  })
  const { upcomingMore } = useUpcomingMore()

  useUpcomingMarkers({
    loading: bookingLoading || orderLoading || trainLoading,
    firstDisplay,
    firstDisplayCount: FIRST_DISPLAY_COUNT,
    upcomingBookingItems,
    upcomingOrderItems,
    upcomingBookingData,
    upcomingOrderData,
  })

  useEffect(() => {
    if (typeof upcomingAnchorId === 'string') {
      const targetElement = document.getElementById(upcomingAnchorId)
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }

      removeQueryStringWithoutRouterChange('upcomingAnchor')
    }
  }, [upcomingAnchorId])

  const init = () => {
    setFirstDisplay(true)
    resetUpcomingBooking()
    resetUpcomingOrder()
    resetUpcomingTrainTicket()
  }

  useEffect(() => {
    // 앵커링은 캐시 초기화 예외
    if (upcomingAnchorId) return
    // 탭 변경시 초기화
    init()
  }, [tab])

  const handleClickMore = (fetchMore: () => void) => {
    sendClickLog({ area: CLICK_CODE['timelinepg.upcomingviewmore'] })
    if (firstDisplay) {
      setFirstDisplay(false)
      onClickMore?.()
      return
    }
    fetchMore()
  }

  if (tab === TimelineTab.Reservation) {
    const upcomingItems = firstDisplay ? upcomingBookingItems.slice(0, FIRST_DISPLAY_COUNT) : upcomingBookingItems
    const hasMore = firstDisplay ? upcomingBookingItems.length > FIRST_DISPLAY_COUNT : upcomingBookingMore

    if (isEmptyArray(upcomingItems)) return null

    return (
      <UITimelinePaginationUpcomingSection
        title="다가오는 예약"
        upcomingItems={upcomingItems}
        upcomingItemCount={upcomingBookingCount}
        hasMore={hasMore}
        onClickMore={() => handleClickMore(upcomingBookingFetchMore)}
      />
    )
  }

  if (tab === TimelineTab.Order) {
    const upcomingItems = firstDisplay ? upcomingOrderItems.slice(0, FIRST_DISPLAY_COUNT) : upcomingOrderItems
    const hasMore = firstDisplay ? upcomingOrderItems.length > FIRST_DISPLAY_COUNT : upcomingOrderMore

    if (isEmptyArray(upcomingItems)) return null

    return (
      <UITimelinePaginationUpcomingSection
        title="진행 중인 주문"
        upcomingItems={upcomingItems}
        upcomingItemCount={upcomingOrderCount}
        hasMore={hasMore}
        onClickMore={() => handleClickMore(upcomingOrderFetchMore)}
      />
    )
  }

  if (tab === TimelineTab.TrainTicket) {
    const upcomingItems = firstDisplay
      ? upcomingTrainTicketItems.slice(0, FIRST_DISPLAY_COUNT)
      : upcomingTrainTicketItems
    const hasMore = firstDisplay ? upcomingTrainTicketItems.length > FIRST_DISPLAY_COUNT : upcomingTrainTicketMore

    if (isEmptyArray(upcomingItems)) return null

    return (
      <UITimelinePaginationUpcomingSection
        title="예매한 승차권"
        upcomingItems={upcomingItems}
        upcomingItemCount={upcomingTrainTicketCount}
        hasMore={hasMore}
        onClickMore={() => handleClickMore(upcomingTrainTicketFetchMore)}
      />
    )
  }

  return (
    <UITimelineAllUpcomingSection
      upcomingBookingCount={upcomingBookingCount}
      upcomingBookingItems={upcomingBookingItems}
      upcomingBookingMore={upcomingBookingMore}
      upcomingOrderCount={upcomingOrderCount}
      upcomingOrderItems={upcomingOrderItems}
      upcomingOrderMore={upcomingOrderMore}
      upcomingTrainTicketCount={upcomingTrainTicketCount}
      upcomingTrainTicketItems={upcomingTrainTicketItems}
      upcomingTrainTicketMore={upcomingTrainTicketMore}
      onClickOrderMore={props => {
        sendClickLog({ area: CLICK_CODE['timelinepg.ordertitle'] })
        onClickMore?.()

        if (props?.anchor) {
          setFirstDisplay(false)
          upcomingMore({ upcomingSectionTab: UPCOMING_SECTION_TAB.ORDER, anchor: true, replace: true })
        }
        upcomingMore({ upcomingSectionTab: UPCOMING_SECTION_TAB.ORDER, replace: true })
      }}
      onClickReservationMore={props => {
        sendClickLog({ area: CLICK_CODE['timelinepg.bookingtitle'] })
        onClickMore?.()

        if (props?.anchor) {
          setFirstDisplay(false)
          upcomingMore({ upcomingSectionTab: UPCOMING_SECTION_TAB.BOOKING, anchor: true, replace: true })
        }
        upcomingMore({ upcomingSectionTab: UPCOMING_SECTION_TAB.BOOKING, replace: true })
      }}
      onClickTrainTicketMore={props => {
        sendClickLog({ area: CLICK_CODE['timelinepg.traintickettitle'] })
        onClickMore?.()

        if (props?.anchor) {
          setFirstDisplay(false)
          upcomingMore({ upcomingSectionTab: UPCOMING_SECTION_TAB.TRAIN_TICKET, anchor: true, replace: true })
        }
        upcomingMore({ upcomingSectionTab: UPCOMING_SECTION_TAB.TRAIN_TICKET, replace: true })
      }}
    />
  )
}
